"use client";

import React from "react";
import { ArrowRight, Activity, CircleCheck as CheckCircle2, TrendingDown, Gauge, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Card, CardContent } from "@/components/ui/card";
export default function DryingHero() {
  const shouldReduceMotion = useReducedMotion();
  const metrics = [{
    label: "Target Endpoint LOD",
    value: "1.8% ± 0.2%",
    status: "Optimal"
  }, {
    label: "Cycle Time Reduction",
    value: "28%",
    status: "Verified"
  }, {
    label: "Spectral Sampling Rate",
    value: "10 Hz",
    status: "In-line"
  }];
  return <section id="dryingendpointhero" data-nav="dark" className="relative bg-sidebar text-sidebar-foreground overflow-hidden py-16 sm:py-20 lg:py-24 border-b border-sidebar-border">
      {/* Subtle background technical grid pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-5 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]" aria-hidden="true" />

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: 8 Cols on lg */}
          <div className="lg:col-span-8 flex flex-col items-start text-left space-y-6 sm:space-y-8">
            
            {/* Breadcrumb Eyebrow */}
            <Breadcrumb>
              <BreadcrumbList className="text-sidebar-foreground/70 text-xs sm:text-sm font-medium">
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/SolutionsPage" className="text-sidebar-foreground/80 hover:text-sidebar-primary-foreground transition-colors">
                      Solutions
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-sidebar-foreground/40" />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/SolutionsPage" className="text-sidebar-foreground/80 hover:text-sidebar-primary-foreground transition-colors">
                      Process Analytical Technology
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-sidebar-foreground/40" />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-sidebar-primary font-semibold">
                    Drying Endpoint
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            {/* Main Headline */}
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sidebar-accent border border-sidebar-border text-sidebar-foreground text-xs font-medium">
                <Activity className="size-3.5 text-sidebar-primary" />
                <span>Real-Time Moisture & Phase Kinetics</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tight text-sidebar-foreground leading-[1.15]">
                Optimizing Drying Endpoints in Pharmaceutical Granulation & Processing
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-sidebar-foreground/80 max-w-2xl leading-relaxed">
                Eliminate over-drying and batch variability with in-line spectroscopic PAT. Continuous multi-point moisture tracking ensures precise endpoint determination in fluid bed and vacuum dryers.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 pt-2 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-semibold px-6 h-12">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <span>Discuss Your Application</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-sidebar-border text-sidebar-foreground bg-sidebar-accent/50 hover:bg-sidebar-accent hover:text-sidebar-foreground h-12 px-6">
                <Link to="/SolutionsPage">
                  Explore PAT Methodologies
                </Link>
              </Button>
            </div>

            {/* Highlights Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 w-full border-t border-sidebar-border/60">
              {metrics.map((metric, i) => <div key={metric.label} data-index={i} className="flex flex-col space-y-1">
                  <span className="text-xs text-sidebar-foreground/70 uppercase tracking-wider font-mono">
                    {metric.label}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xl sm:text-2xl font-bold font-mono text-sidebar-foreground">
                      {metric.value}
                    </span>
                    <Badge variant="secondary" className="text-[10px] bg-sidebar-accent text-sidebar-foreground border-sidebar-border">
                      {metric.status}
                    </Badge>
                  </div>
                </div>)}
            </div>

          </div>

          {/* Right Column: 4 Cols on lg (Drying-Kinetic-Curve Schematic) */}
          <div className="lg:col-span-4 w-full">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: "easeOut"
          }}>
              <Card className="bg-sidebar-accent/70 border-sidebar-border shadow-xl backdrop-blur-sm text-sidebar-foreground overflow-hidden">
                <CardContent className="p-5 sm:p-6 space-y-5">
                  
                  {/* Schematic Header */}
                  <div className="flex items-center justify-between border-b border-sidebar-border/80 pb-3">
                    <div className="flex items-center gap-2">
                      <Gauge className="size-4 text-sidebar-primary" />
                      <span className="font-semibold text-sm tracking-wide">Drying Kinetic Trajectory</span>
                    </div>
                    <Badge className="bg-sidebar-primary text-sidebar-primary-foreground text-[10px] uppercase font-mono px-2 py-0.5">
                      LIVE PAT STREAM
                    </Badge>
                  </div>

                  {/* SVG Kinetic Curve Schematic */}
                  <div className="relative bg-sidebar/80 rounded-lg p-3 sm:p-4 border border-sidebar-border">
                    <div className="flex justify-between items-center text-[10px] font-mono text-sidebar-foreground/60 mb-2">
                      <span>LOD Moisture (%)</span>
                      <span>Target: &lt; 2.0%</span>
                    </div>

                    <svg viewBox="0 0 320 180" className="w-full h-auto overflow-visible select-none" aria-label="Moisture Trajectory Curve showing drying phases">
                      {/* Grid Lines */}
                      <line x1="30" y1="20" x2="300" y2="20" stroke="currentColor" strokeOpacity="0.1" strokeDasharray="3 3" />
                      <line x1="30" y1="60" x2="300" y2="60" stroke="currentColor" strokeOpacity="0.1" strokeDasharray="3 3" />
                      <line x1="30" y1="100" x2="300" y2="100" stroke="currentColor" strokeOpacity="0.1" strokeDasharray="3 3" />
                      <line x1="30" y1="140" x2="300" y2="140" stroke="currentColor" strokeOpacity="0.2" />

                      {/* Axes */}
                      <line x1="30" y1="10" x2="30" y2="145" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1.5" />
                      <line x1="25" y1="140" x2="310" y2="140" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1.5" />

                      {/* Y-Axis Labels */}
                      <text x="22" y="24" textAnchor="end" fill="currentColor" fillOpacity="0.6" fontSize="9" fontFamily="monospace">12%</text>
                      <text x="22" y="64" textAnchor="end" fill="currentColor" fillOpacity="0.6" fontSize="9" fontFamily="monospace">8%</text>
                      <text x="22" y="104" textAnchor="end" fill="currentColor" fillOpacity="0.6" fontSize="9" fontFamily="monospace">4%</text>
                      <text x="22" y="143" textAnchor="end" fill="currentColor" fillOpacity="0.6" fontSize="9" fontFamily="monospace">0%</text>

                      {/* X-Axis Labels */}
                      <text x="30" y="156" textAnchor="middle" fill="currentColor" fillOpacity="0.6" fontSize="9" fontFamily="monospace">0m</text>
                      <text x="120" y="156" textAnchor="middle" fill="currentColor" fillOpacity="0.6" fontSize="9" fontFamily="monospace">15m</text>
                      <text x="210" y="156" textAnchor="middle" fill="currentColor" fillOpacity="0.6" fontSize="9" fontFamily="monospace">30m</text>
                      <text x="290" y="156" textAnchor="middle" fill="currentColor" fillOpacity="0.6" fontSize="9" fontFamily="monospace">45m</text>

                      {/* Endpoint Threshold Shaded Band */}
                      <rect x="230" y="115" width="70" height="25" fill="currentColor" fillOpacity="0.08" rx="2" />
                      <line x1="230" y1="126" x2="300" y2="126" stroke="currentColor" strokeOpacity="0.3" strokeDasharray="2 2" />

                      {/* Kinetic Decay Curve */}
                      <path d="M 35 24 C 70 25, 90 40, 130 75 C 170 110, 210 125, 270 126 L 295 127" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-chart-1" />

                      {/* Endpoint Marker Dot */}
                      <circle cx="270" cy="126" r="4.5" fill="currentColor" className="text-primary" />
                      <circle cx="270" cy="126" r="8" fill="none" stroke="currentColor" strokeWidth="1" className="text-primary opacity-60 animate-ping" />

                      {/* Annotations */}
                      <text x="80" y="38" fill="currentColor" fillOpacity="0.8" fontSize="8" fontWeight="600">Phase I: Warm-up</text>
                      <text x="145" y="70" fill="currentColor" fillOpacity="0.8" fontSize="8" fontWeight="600">Phase II: Constant Rate</text>
                      <text x="235" y="110" fill="currentColor" className="text-primary font-mono text-[9px] font-bold">Endpoint 1.8%</text>
                    </svg>

                    <div className="mt-3 pt-3 border-t border-sidebar-border/60 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5 text-sidebar-foreground">
                        <CheckCircle2 className="size-3.5 text-sidebar-primary" />
                        <span className="font-medium">Automatic Shutoff Triggered</span>
                      </div>
                      <span className="font-mono text-sidebar-foreground/70 text-[11px]">t = 38.4 min</span>
                    </div>
                  </div>

                  {/* Operational Benefits Mini-List */}
                  <div className="space-y-2 pt-1 text-xs">
                    <div className="flex items-start gap-2 text-sidebar-foreground/90">
                      <TrendingDown className="size-3.5 text-sidebar-primary shrink-0 mt-0.5" />
                      <span>Prevents thermal degradation of sensitive active ingredients.</span>
                    </div>
                    <div className="flex items-start gap-2 text-sidebar-foreground/90">
                      <Sparkles className="size-3.5 text-sidebar-primary shrink-0 mt-0.5" />
                      <span>Eliminates manual thief sampling and containment breaches.</span>
                    </div>
                  </div>

                </CardContent>
              </Card>
            </motion.div>
          </div>

        </div>
      </div>
    </section>;
}